<template>
  <div class="profile-page" :class="{ 'page-is-busy': pageLoading }">
    <!-- 背景渐变 -->
    <div class="bg-gradient"></div>

    <!-- 全页加载反馈 -->
    <div v-if="pageLoading" class="page-loading-mask" aria-live="polite" aria-busy="true">
      <div class="page-loading-box">
        <span class="page-loading-spinner" aria-hidden="true"></span>
        <p class="page-loading-text">{{ pageLoadingText }}</p>
      </div>
    </div>

    <!-- 顶部导航 -->
    <div class="header">
      <div class="back-btn btn-press" :class="{ 'is-disabled': navLoading }" @click="goBack">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </div>
      <h1 class="title">个人中心</h1>
      <div class="placeholder"></div>
    </div>

    <!-- 用户信息卡片 -->
    <div class="user-card">
      <div class="avatar-wrap" @click="openAvatarPreview">
        <img
          :src="userInfo.avatar"
          alt="头像"
          class="avatar"
          loading="lazy"
          decoding="async"
          @error="onAvatarError"
        >
      </div>
      <div class="user-info">
        <div class="username-row">
          <h2 class="username">{{ userInfo.name }}</h2>
          <span class="verify-tag" :class="verifyTagClass">
            {{ authLabel }}
          </span>
        </div>
        <p class="account">账号：{{ userInfo.account }}</p>
      </div>
    </div>

    <!-- 仅未认证 / 认证失败 / 等待用户认证时提示填写实名信息 -->
    <div v-if="showAuthSubmitTip" class="auth-four-tip" role="status">
      <span class="auth-four-tip__icon" aria-hidden="true">!</span>
      <div class="auth-four-tip__body">
        <p class="auth-four-tip__title">请用户实名认证</p>
        <p v-if="authCode === 3" class="auth-four-tip__desc">
          审核未通过：{{ autoFailMessage || '管理员未填写具体原因' }}。请根据原因修改并重新提交实名认证信息。
        </p>
        <p v-else class="auth-four-tip__desc">
          欢迎新用户加入金融自媒体人才，为保障账号正常使用，麻烦您完成实名认证、提交实名资料。
        </p>
        <div class="auth-four-tip__actions">
          <button type="button" class="auth-fill-btn btn-press" @click="openAuthFormModal">
            填写实名认证信息
          </button>
        </div>
      </div>
    </div>

    <!-- 我的认证模块 -->
    <div class="cert-section">
      <div class="cert-section-header">
        <h3 class="section-title">我的认证</h3>
        <button
          type="button"
          class="cert-refresh-btn btn-press"
          :class="{ 'is-loading': certRefreshing }"
          :disabled="certRefreshing || pageLoading"
          @click="refreshAuthFromServer"
        >
          {{ certRefreshing ? '刷新中…' : '刷新认证状态' }}
        </button>
      </div>
      <div class="cert-card" :class="{ 'is-loading': certRefreshing }">
        <div
          v-if="certImageLoading && !certImageSrc"
          class="cert-loading-placeholder"
        >
          <span class="cert-loading-spinner" aria-hidden="true"></span>
          <span>加载认证信息…</span>
        </div>
        <div
          v-else-if="certImageSrc"
          class="cert-image-wrap"
          role="button"
          tabindex="0"
          title="点击查看大图"
          @click="openCertPreview"
          @keydown.enter="openCertPreview"
          @keydown.space.prevent="openCertPreview"
        >
          <img
            :src="certImageSrc"
            alt="认证图片"
            class="cert-image"
            loading="lazy"
            decoding="async"
            @error="onCertImageError"
          >
        </div>
        <div v-else class="cert-placeholder">未认证</div>
        <div class="cert-status">
          <span class="status-dot" :class="{ active: isAuthPass }"></span>
          <span class="status-text">{{ certStatusLine }}</span>
        </div>
      </div>
    </div>

    <!-- 认证图大图预览 -->
    <div
      v-show="certPreviewVisible"
      class="cert-modal-mask"
      @click.self="closeCertPreview"
    >
      <div class="cert-modal-box" @click.stop>
        <button type="button" class="cert-modal-close" aria-label="关闭" @click="closeCertPreview">×</button>
        <p class="cert-modal-title">认证图片</p>
        <div class="cert-modal-img-wrap">
          <img
            :src="certImageSrc"
            alt="认证图片预览"
            class="cert-modal-img"
            loading="lazy"
            decoding="async"
            @error="onCertPreviewError"
          >
        </div>
        <div class="cert-modal-actions">
          <button
            type="button"
            class="cert-download-btn btn-press"
            :class="{ 'is-loading': certDownloading }"
            :disabled="certDownloading"
            @click="downloadCertImage"
          >
            {{ certDownloading ? '准备下载…' : '下载认证图片' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 问卷修改 -->
    <div class="menu-section">
      <button
        type="button"
        class="menu-item btn-press"
        :class="{ 'is-loading': navLoading }"
        :disabled="navLoading || pageLoading"
        @click="goSurveyEdit"
      >
        <span class="menu-item-label">问卷修改</span>
        <svg class="menu-item-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>
    </div>

    <!-- 底部退出按钮 -->
    <div
      class="logout-btn btn-press"
      :class="{ 'is-loading': navLoading }"
      @click="logout"
    >
      {{ navLoading ? '退出中…' : '退出登录' }}
    </div>

    <!-- 填写认证信息（提交后 is_auth → 正在审核） -->
    <div
      v-show="authFormModalVisible"
      class="cert-modal-mask auth-form-mask"
      @click.self="closeAuthFormModal"
    >
      <div class="auth-form-modal-box" role="dialog" aria-modal="true" aria-labelledby="auth-form-title" @click.stop>
        <button type="button" class="cert-modal-close" aria-label="关闭" @click="closeAuthFormModal">×</button>
        <p id="auth-form-title" class="cert-modal-title">填写认证信息</p>
        <div
          v-if="authFormInlineError"
          class="auth-form-inline-error"
          role="alert"
          aria-live="polite"
        >
          {{ authFormInlineError }}
        </div>
        <div class="auth-form-fields">
          <label class="auth-form-label">
            <span class="auth-form-label-text">姓名</span>
            <input
              v-model.trim="authForm.user_name"
              type="text"
              class="auth-form-input"
              placeholder="请输入真实姓名"
              maxlength="32"
              autocomplete="name"
              @input="clearAuthFormInlineError"
            >
          </label>
          <label class="auth-form-label">
            <span class="auth-form-label-text">手机号</span>
            <input
              v-model.trim="authForm.user_phone_number"
              type="tel"
              class="auth-form-input"
              placeholder="11 位手机号码"
              maxlength="11"
              autocomplete="tel"
              @input="clearAuthFormInlineError"
            >
          </label>
        </div>
        <div class="auth-form-actions">
          <button type="button" class="auth-form-btn ghost btn-press" :disabled="authFormSubmitting" @click="closeAuthFormModal">
            取消
          </button>
          <button
            type="button"
            class="auth-form-btn primary btn-press"
            :class="{ 'is-loading': authFormSubmitting }"
            :disabled="authFormSubmitting"
            @click="submitAuthForm"
          >
            {{ authFormSubmitting ? '提交中…' : '提交' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 头像预览：大图 + 上传 -->
    <div
      v-show="avatarPreviewVisible"
      class="avatar-modal-mask"
      @click.self="closeAvatarPreview"
    >
      <div class="avatar-modal" @click.stop>
        <button type="button" class="avatar-modal-close" aria-label="关闭" @click="closeAvatarPreview">×</button>
        <p class="avatar-modal-title">我的头像</p>
        <div class="avatar-modal-img-wrap">
          <img
            :src="userInfo.avatar"
            alt="头像预览"
            class="avatar-modal-img"
            loading="lazy"
            decoding="async"
            @error="onAvatarError"
          >
        </div>
        <div class="avatar-modal-actions">
          <button
            type="button"
            class="avatar-modal-btn primary btn-press"
            :class="{ 'is-loading': avatarUploading }"
            :disabled="avatarUploading"
            @click="triggerAvatarUpload"
          >
            {{ avatarUploading ? '上传中…' : '自己上传头像' }}
          </button>
        </div>
        <input
          ref="avatarFileInput"
          type="file"
          class="avatar-file-input"
          accept="image/jpeg,image/png,image/gif,image/webp"
          @change="onAvatarFileChange"
        >
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { clearLoginSession, consumeCertSnapshotPending } from '@/utils/loginSession'

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024
/** 与 py_/data/user_head.png 同源，供首屏/缺省占位（public/user_head.png） */
function defaultUserHeadSrc() {
  const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
  return `${base}user_head.png`
}

/** 与 Login.vue 一致：未设置 VUE_APP_API_BASE 时为同源 `/api…`（npm run serve 走代理） */
function h5ApiUrl(path) {
  const base = (process.env.VUE_APP_API_BASE || '').trim().replace(/\/$/, '')
  const p = path.startsWith('/') ? path : '/' + path
  return base ? base + p : p
}

export default {
  name: 'UserCenter',
  data() {
    return {
      userInfo: {
        name: '访客',
        account: '—',
        avatar: defaultUserHeadSrc()
      },
      /** 后端 is_auth：0 未认证 1 正在审核 2 认证通过 3 认证失败 4 等待用户认证 */
      authCode: 0,
      authLabel: '未认证',
      certImage: '',
      autoFailMessage: '',
      certStatusLine: '未认证',
      certPreviewVisible: false,
      avatarPreviewVisible: false,
      certRefreshing: false,
      /** 登录后首次拉取认证图期间为 true，避免误报加载失败 */
      certSnapshotLoading: false,
      avatarUploading: false,
      certDownloading: false,
      navLoading: false,
      loginUserId: '',
      isFirstLogin: false,
      authFormModalVisible: false,
      authFormSubmitting: false,
      /** 认证弹窗内联错误（避免 Toast 被遮罩挡住） */
      authFormInlineError: '',
      authForm: {
        user_name: '',
        user_phone_number: ''
      }
    }
  },
  computed: {
    certImageSrc() {
      return this.normalizeCertImageSrc(this.certImage)
    },
    certImageLoading() {
      return this.certRefreshing || this.certSnapshotLoading
    },
    isAuthPass() {
      return this.authCode === 2
    },
    /** 注册提交问卷或管理员已通过时不再提示重复填写 */
    showAuthSubmitTip() {
      return this.authCode === 0 || this.authCode === 3 || this.authCode === 4
    },
    verifyTagClass() {
      if (this.authCode === 2) return { verified: true }
      if (this.authCode === 1 || this.authCode === 4) return { pending: true }
      if (this.authCode === 3) return { rejected: true }
      return {}
    },
    pageLoading() {
      return (
        this.authFormSubmitting ||
        this.certRefreshing ||
        this.avatarUploading ||
        this.certDownloading ||
        this.navLoading
      )
    },
    pageLoadingText() {
      if (this.authFormSubmitting) return '正在提交认证信息…'
      if (this.certRefreshing) return '正在刷新认证状态…'
      if (this.avatarUploading) return '正在上传头像…'
      if (this.certDownloading) return '正在准备下载…'
      if (this.navLoading) return '正在处理…'
      return '请稍候…'
    }
  },
  mounted() {
    if (consumeCertSnapshotPending()) {
      this.certSnapshotLoading = true
    }
    this.applyLoginUser()
    this.loadUserSnapshotSilently()
    this.$nextTick(() => {
      if (!this.showAuthSubmitTip) return
      if (this.authCode === 4) {
        Toast({
          message: '请用户实名认证',
          duration: 4000
        })
        return
      }
      if (this.isFirstLogin) {
        Toast({
          message: '首次登录，请填写并提交实名认证信息',
          duration: 4000
        })
      }
    })
  },
  methods: {
    /** 登录/接口返回的头像：data URL、网络 URL、相对路径、或裸 base64 → 可用作 img src */
    normalizeUserHeadImg(s) {
      if (!s || typeof s !== 'string') return ''
      const t = s.trim()
      if (!t) return ''
      if (t === '/api/default_user_head.png') return ''
      if (t.startsWith('data:image') || /^https?:\/\//i.test(t)) return t
      if (t.startsWith('/api/')) return h5ApiUrl(t)
      if (t.startsWith('/') || t.startsWith('./')) return t
      if (t.length > 80 && /^[A-Za-z0-9+/=\s]+$/.test(t)) {
        return 'data:image/png;base64,' + t.replace(/\s/g, '')
      }
      return t
    },
    /** 与后台 auto_img 一致：data URL、http(s)、或裸 base64 */
    normalizeCertImageSrc(s) {
      if (!s || typeof s !== 'string') return ''
      const t = s.trim()
      if (!t) return ''
      if (t.startsWith('data:image') || /^https?:\/\//i.test(t)) return t
      if (t.length > 80 && /^[A-Za-z0-9+/=\s]+$/.test(t)) {
        return 'data:image/png;base64,' + t.replace(/\s/g, '')
      }
      return ''
    },
    /** 将头像写入数据库，并合并返回的 user_info 到 loginUser */
    async persistAvatarToServer(headImg, { silent = false } = {}) {
      const uid = (this.loginUserId || '').trim()
      if (!uid) {
        if (!silent) Toast.fail('未登录')
        return false
      }
      const payload = headImg == null ? '' : String(headImg).trim()
      if (!payload) {
        if (!silent) Toast.fail('头像数据无效')
        return false
      }
      try {
        const res = await fetch(h5ApiUrl('/api/change_avatar'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: uid, user_head_img: payload })
        })
        const data = await res.json().catch(() => ({}))
        if (!data.success) {
          if (!silent) Toast.fail(data.message || '头像保存失败')
          return false
        }
        if (data.user_info && typeof data.user_info === 'object') {
          try {
            const raw = sessionStorage.getItem('loginUser')
            const prev = raw ? JSON.parse(raw) : {}
            sessionStorage.setItem(
              'loginUser',
              JSON.stringify({ ...prev, ...data.user_info })
            )
          } catch (_) {
            /* ignore */
          }
          const h = data.user_info.user_head_img
          if (h != null && String(h).trim() !== '') {
            this.userInfo.avatar =
              this.normalizeUserHeadImg(String(h).trim()) || this.userInfo.avatar
          }
        }
        return true
      } catch (_) {
        if (!silent) Toast.fail('网络异常，头像未保存')
        return false
      }
    },
    clearAuthFormInlineError() {
      this.authFormInlineError = ''
    },
    openAuthFormModal() {
      this.authFormInlineError = ''
      this.authFormModalVisible = true
      try {
        const raw = sessionStorage.getItem('loginUser')
        if (!raw) return
        const u = JSON.parse(raw)
        const a =
          u.auth_info && typeof u.auth_info === 'object' ? u.auth_info : {}
        this.authForm.user_name = (a.user_name || '').trim()
        const p = (a.user_phone_number || '').trim()
        if (p) {
          this.authForm.user_phone_number = p
        } else {
          const ac = String(u.user_phone || '').replace(/\D/g, '')
          if (ac.length === 11) this.authForm.user_phone_number = ac
        }
      } catch (_) {
        /* ignore */
      }
    },
    closeAuthFormModal() {
      this.authFormModalVisible = false
      this.authFormInlineError = ''
    },
    consumeFirstLoginFlag() {
      this.isFirstLogin = false
      try {
        sessionStorage.setItem('h5_first_login', '0')
        const raw = sessionStorage.getItem('loginUser')
        if (!raw) return
        const prev = JSON.parse(raw)
        if (!prev || typeof prev !== 'object') return
        sessionStorage.setItem(
          'loginUser',
          JSON.stringify({ ...prev, first_login: false })
        )
      } catch (_) {
        /* ignore */
      }
    },
    async submitAuthForm() {
      this.authFormInlineError = ''
      const uid = (this.loginUserId || '').trim()
      if (!uid) {
        this.authFormInlineError = '未登录，请重新登录后再试'
        return
      }
      const user_name = (this.authForm.user_name || '').trim()
      const user_phone_number = (this.authForm.user_phone_number || '').trim()
      if (!user_name) {
        this.authFormInlineError = '请填写姓名'
        return
      }
      const digits = user_phone_number.replace(/\D/g, '')
      if (!/^1[3-9]\d{9}$/.test(digits)) {
        this.authFormInlineError = '请输入正确的 11 位手机号（1 开头）'
        return
      }

      this.authFormSubmitting = true
      try {
        const res = await fetch(h5ApiUrl('/api/auth_user'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: uid,
            user_name,
            user_phone_number: digits
          })
        })
        const text = await res.text()
        let data = {}
        try {
          data = text ? JSON.parse(text) : {}
        } catch (_) {
          this.authFormInlineError =
            res.status === 404
              ? '接口不存在，请确认后端已部署 /api/auth_user'
              : `服务返回异常（HTTP ${res.status}）`
          return
        }
        if (!res.ok) {
          this.authFormInlineError =
            data.message || `请求失败（${res.status}）`
          return
        }
        if (!data.success || !data.user_info) {
          this.authFormInlineError = data.message || '提交失败'
          return
        }
        try {
          const raw = sessionStorage.getItem('loginUser')
          const prev = raw ? JSON.parse(raw) : {}
          sessionStorage.setItem(
            'loginUser',
            JSON.stringify({ ...prev, ...data.user_info })
          )
        } catch (_) {
          /* ignore */
        }
        this.applyLoginUser()
        this.consumeFirstLoginFlag()
        this.authFormInlineError = ''
        this.closeAuthFormModal()
        Toast.success(data.message || '已提交')
      } catch (e) {
        this.authFormInlineError =
          (e && e.message) || '网络异常，请检查网络后重试'
      } finally {
        this.authFormSubmitting = false
      }
    },
    async refreshAuthFromServer({ silent = false } = {}) {
      const uid = (this.loginUserId || '').trim()
      if (!uid) {
        if (!silent) Toast.fail('未登录')
        return false
      }
      if (!silent) this.certRefreshing = true
      try {
        const res = await fetch(h5ApiUrl('/api/user_auth_snapshot'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: uid })
        })
        const text = await res.text()
        let data = {}
        try {
          data = text ? JSON.parse(text) : {}
        } catch (_) {
          if (!silent) {
            Toast.fail(
              res.status === 404
                ? '接口不存在（404）：请在本机重启含 user_auth_snapshot 的 API，或将 vue.config 里 /api 代理指向正在运行的后端端口'
                : `服务返回非 JSON（HTTP ${res.status}），多为代理未指到后端或返回了网页`
            )
          }
          return false
        }
        if (!res.ok) {
          if (!silent) Toast.fail(data.message || `请求失败（HTTP ${res.status}）`)
          return false
        }
        if (!data.success || !data.user_info) {
          const msg = String(data.message || '').trim()
          if (msg === '用户不存在') {
            this.clearSessionAndGoHome({ silent, message: msg })
            return false
          }
          if (!silent) Toast.fail(msg || '刷新失败')
          return false
        }
        this.mergeUserSnapshot(data.user_info)
        if (!silent) Toast.success('认证状态已更新')
        return true
      } catch (e) {
        if (!silent) Toast.fail((e && e.message) || '网络异常，请稍后重试')
        return false
      } finally {
        if (!silent) this.certRefreshing = false
      }
    },
    mergeUserSnapshot(userInfo) {
      const info = userInfo && typeof userInfo === 'object' ? userInfo : null
      if (!info) return
      const slimHead =
        info.user_head_img != null && String(info.user_head_img).trim().startsWith('data:image')
          ? ''
          : info.user_head_img
      try {
        const raw = sessionStorage.getItem('loginUser')
        const prev = raw ? JSON.parse(raw) : {}
        const next = {
          ...prev,
          user_id: info.user_id || prev.user_id || '',
          user_phone: info.user_phone != null ? info.user_phone : prev.user_phone,
          user_head_img: slimHead != null ? slimHead : prev.user_head_img,
          username: info.username != null ? info.username : prev.username,
          is_auth: info.is_auth != null ? info.is_auth : prev.is_auth,
          is_auth_label: info.is_auth_label != null ? info.is_auth_label : prev.is_auth_label,
          first_login: info.first_login != null ? info.first_login : prev.first_login,
          auto_fail_message:
            info.auto_fail_message != null ? info.auto_fail_message : prev.auto_fail_message,
        }
        sessionStorage.setItem('loginUser', JSON.stringify(next))
      } catch (_) {
        /* ignore */
      }
      this.applyLoginUser()
      const head = (info.user_head_img || '').trim()
      if (head) {
        this.userInfo.avatar =
          this.normalizeUserHeadImg(head) || defaultUserHeadSrc()
      }
      const img = (info.auto_img || '').trim()
      if (img && Number(info.is_auth) === 2) {
        this.certImage = img
      } else if (Number(info.is_auth) !== 2) {
        this.certImage = ''
      }
    },
    async loadUserSnapshotSilently() {
      const uid = (this.loginUserId || '').trim()
      if (!uid) {
        this.certSnapshotLoading = false
        return
      }
      try {
        await this.refreshAuthFromServer({ silent: true })
      } finally {
        this.certSnapshotLoading = false
      }
    },
    clearSessionAndGoHome({ silent = false, message = '用户不存在' } = {}) {
      const uid = (this.loginUserId || '').trim()
      clearLoginSession(uid)
      if (!silent && message) Toast.fail(message)
      this.$router.replace({ name: 'lanhu_huaban1' })
    },
    applyLoginUser() {
      let raw
      try {
        raw = sessionStorage.getItem('loginUser')
      } catch (_) {
        return
      }
      if (!raw) return
      let u
      try {
        u = JSON.parse(raw)
      } catch (_) {
        return
      }
      if (!u || typeof u !== 'object') return

      this.loginUserId = (u.user_id || '').trim()
      let firstLoginFlag = false
      try {
        firstLoginFlag = sessionStorage.getItem('h5_first_login') === '1'
      } catch (_) {
        firstLoginFlag = false
      }
      this.isFirstLogin = !!u.first_login || firstLoginFlag
      this.userInfo.name = (u.username || '访客').trim() || '访客'
      this.userInfo.account = (u.user_phone || '—').trim() || '—'
      const head = (u.user_head_img || '').trim()
      this.userInfo.avatar =
        this.normalizeUserHeadImg(head) || defaultUserHeadSrc()

      try {
        const n = Number(u.is_auth)
        this.authCode = Number.isNaN(n) ? 0 : Math.max(0, Math.min(4, n))
      } catch (_) {
        this.authCode = 0
      }
      this.authLabel = (u.is_auth_label || '未认证').trim() || '未认证'
      this.certStatusLine = this.authLabel

      const img = (u.auto_img || '').trim()
      if (img && this.authCode === 2) {
        this.certImage = img
      } else if (this.authCode !== 2) {
        this.certImage = ''
      }
      const failMsgFromAuthInfo =
        u.auth_info && typeof u.auth_info === 'object'
          ? String(u.auth_info.auto_fail_message || '').trim()
          : ''
      this.autoFailMessage = (u.auto_fail_message || failMsgFromAuthInfo || '').trim()

      if (this.isFirstLogin && (this.authCode === 1 || this.authCode === 2)) {
        this.consumeFirstLoginFlag()
      }
    },
    onCertImageError() {
      if (this.certImageLoading) {
        this.certImage = ''
        return
      }
      if (Number(this.authCode) === 2) {
        Toast.fail('认证图片加载失败')
      }
      this.certImage = ''
    },
    onCertPreviewError() {
      if (this.certImageLoading) {
        this.closeCertPreview()
        return
      }
      if (Number(this.authCode) === 2) {
        Toast.fail('认证图片加载失败')
      }
      this.closeCertPreview()
    },
    openCertPreview() {
      if (!this.certImageSrc) return
      this.certPreviewVisible = true
    },
    async downloadCertImage() {
      if (this.certDownloading) return
      if (!this.certImageSrc) {
        Toast.fail('暂无可下载的认证图片')
        return
      }
      this.certDownloading = true
      const ts = new Date()
      const stamp = `${ts.getFullYear()}${String(ts.getMonth() + 1).padStart(2, '0')}${String(ts.getDate()).padStart(2, '0')}`
      const fileName = `认证图片_${stamp}.png`
      try {
        let blob = null
        if (this.certImageSrc.startsWith('data:image')) {
          const resp = await fetch(this.certImageSrc)
          blob = await resp.blob()
        } else {
          const res = await fetch(this.certImageSrc)
          if (!res.ok) {
            Toast.fail('下载失败，请稍后重试')
            return
          }
          blob = await res.blob()
        }
        if (!blob) {
          Toast.fail('下载失败，请稍后重试')
          return
        }

        const isMobile =
          /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '')
        const isSafari =
          /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '')

        // 手机端优先走系统分享（可保存到相册/文件）
        if (
          isMobile &&
          navigator.canShare &&
          navigator.share &&
          typeof File !== 'undefined'
        ) {
          const file = new File([blob], fileName, { type: blob.type || 'image/png' })
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: '认证图片',
              text: '请保存认证图片'
            })
            Toast.success('请在系统面板中保存图片')
            return
          }
        }

        const objectUrl = URL.createObjectURL(blob)

        // iOS Safari 对 download 支持弱，改为新开图片页让用户长按保存
        if (isMobile && isSafari) {
          window.open(objectUrl, '_blank')
          Toast.success('已打开图片，请长按图片保存到相册')
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
          return
        }

        const a = document.createElement('a')
        a.href = objectUrl
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(objectUrl)
        Toast.success('已开始下载')
      } catch (_) {
        Toast.fail('下载失败，请稍后重试')
      } finally {
        this.certDownloading = false
      }
    },
    closeCertPreview() {
      this.certPreviewVisible = false
    },
    openAvatarPreview() {
      this.avatarPreviewVisible = true
    },
    closeAvatarPreview() {
      this.avatarPreviewVisible = false
    },
    triggerAvatarUpload() {
      const el = this.$refs.avatarFileInput
      if (el) el.click()
    },
    onAvatarFileChange(e) {
      const file = e.target.files && e.target.files[0]
      if (e.target) e.target.value = ''
      if (!file) return
      if (!/^image\//.test(file.type)) {
        Toast.fail('请选择图片文件')
        return
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        Toast.fail('图片请小于 100MB')
        return
      }
      const reader = new FileReader()
      reader.onload = async () => {
        const dataUrl = reader.result
        if (typeof dataUrl !== 'string') return
        const prev = this.userInfo.avatar
        this.userInfo.avatar = dataUrl
        this.avatarUploading = true
        try {
          const ok = await this.persistAvatarToServer(dataUrl, { silent: true })
          if (ok) {
            Toast.success('头像已更新')
          } else {
            this.userInfo.avatar = prev
            Toast.fail('头像未保存到服务器')
          }
        } finally {
          this.avatarUploading = false
        }
      }
      reader.onerror = () => Toast.fail('读取文件失败')
      reader.readAsDataURL(file)
    },
    onAvatarError() {
      this.userInfo.avatar = defaultUserHeadSrc()
    },
    async goBack() {
      if (this.navLoading) return
      this.navLoading = true
      try {
        await this.$router.push({ name: 'lanhu_huaban1' })
      } finally {
        this.navLoading = false
      }
    },
    async goSurveyEdit() {
      if (this.navLoading) return
      const uid = (this.loginUserId || '').trim()
      if (!uid) {
        Toast.fail('未登录，请先登录')
        this.$router.replace({ name: 'login' })
        return
      }
      this.navLoading = true
      try {
        await this.$router.push({ name: 'surveyEdit' })
      } finally {
        this.navLoading = false
      }
    },
    async logout() {
      if (this.navLoading) return
      this.navLoading = true
      try {
        clearLoginSession((this.loginUserId || '').trim())
        await this.$router.replace({ name: 'lanhu_huaban1' })
      } finally {
        this.navLoading = false
      }
    }
  }
}
</script>

<style scoped>
/* 全局样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.profile-page {
  min-height: 100vh;
  width: 100%;
  max-width: 430px; /* 手机端最大宽度 */
  margin: 0 auto;
  position: relative;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: #fff;
  padding-bottom: 40px;
}
.profile-page.page-is-busy .user-card,
.profile-page.page-is-busy .cert-section,
.profile-page.page-is-busy .menu-section {
  opacity: 0.88;
  transition: opacity 0.2s ease;
}
.page-loading-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 8, 28, 0.45);
  backdrop-filter: blur(2px);
}
.page-loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 22px 28px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
}
.page-loading-spinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.22);
  border-top-color: #fff;
  animation: page-spin 0.75s linear infinite;
}
.page-loading-text {
  margin: 0;
  color: #fff;
  font-size: 14px;
  letter-spacing: 0.02em;
}
@keyframes page-spin {
  to { transform: rotate(360deg); }
}
.btn-press {
  transition: transform 0.12s ease, opacity 0.2s ease, background 0.2s ease;
}
.btn-press:active:not(:disabled):not(.is-disabled) {
  transform: scale(0.98);
}
.btn-press.is-loading,
.btn-press:disabled {
  opacity: 0.78;
  cursor: not-allowed;
}
.back-btn.is-disabled {
  opacity: 0.55;
  pointer-events: none;
}

.bg-gradient {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #1a1a3a 0%, #3a2a6a 50%, #6a4aaa 100%);
  z-index: -1;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  height: 56px;
}

.back-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255,255,255,0.1);
  cursor: pointer;
}

.title {
  font-size: 18px;
  font-weight: 600;
}

.placeholder {
  width: 32px;
}

/* 用户信息卡片 */
.user-card {
  display: flex;
  align-items: center;
  padding: 24px 20px;
  background: rgba(255,255,255,0.08);
  border-radius: 16px;
  margin: 16px;
  backdrop-filter: blur(10px);
}

.avatar-wrap {
  flex-shrink: 0;
  margin-right: 16px;
  cursor: pointer;
}

.avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255,255,255,0.2);
  display: block;
  pointer-events: none;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.username-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.verify-tag {
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  background: #ff4d4f;
  color: #fff;
}

.verify-tag.verified {
  background: #52c41a;
}

.verify-tag.pending {
  background: #faad14;
  color: #1a1a1a;
}

.verify-tag.rejected {
  background: #ff4d4f;
}

.user-info .username {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-info .account {
  font-size: 14px;
  color: rgba(255,255,255,0.7);
}

/* 等待用户认证（is_auth=4）提示条 */
.auth-four-tip {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0 16px 8px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(250, 173, 20, 0.45);
  background: rgba(250, 173, 20, 0.12);
  color: rgba(255, 255, 255, 0.95);
}

.auth-four-tip__icon {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #faad14;
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 800;
  line-height: 22px;
  text-align: center;
}

.auth-four-tip__body {
  flex: 1;
  min-width: 0;
}

.auth-four-tip__title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: #ffc53d;
}

.auth-four-tip__desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.82);
}

.auth-four-tip__actions {
  margin-top: 12px;
}

.auth-fill-btn {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  background: linear-gradient(135deg, #ffc53d, #faad14);
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
}

.auth-fill-btn:active {
  opacity: 0.92;
}

/* 填写认证弹窗 */
.auth-form-mask {
  z-index: 2200;
}

.auth-form-modal-box {
  position: relative;
  width: 100%;
  max-width: min(100%, 380px);
  max-height: calc(100vh - 32px);
  overflow: auto;
  border-radius: 16px;
  background: linear-gradient(160deg, #2d2654 0%, #1a1530 100%);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
  padding-bottom: 16px;
}

.auth-form-inline-error {
  margin: 0 18px 14px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  color: #ffccc7;
  background: rgba(255, 77, 79, 0.22);
  border: 1px solid rgba(255, 120, 117, 0.55);
  border-radius: 10px;
}

.auth-form-fields {
  padding: 0 18px 8px;
}

.auth-form-label {
  display: block;
  margin-bottom: 14px;
}

.auth-form-label-text {
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 6px;
}

.auth-form-input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  font-size: 15px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  font-family: inherit;
}

.auth-form-input::placeholder {
  color: rgba(255, 255, 255, 0.35);
}

.auth-form-input:focus {
  outline: none;
  border-color: rgba(167, 139, 250, 0.65);
}

.auth-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 8px 18px 0;
  flex-wrap: wrap;
}

.auth-form-btn {
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  border: none;
}

.auth-form-btn.ghost {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
}

.auth-form-btn.primary {
  background: linear-gradient(90deg, #a78bfa, #7c3aed);
  color: #fff;
}

.auth-form-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.menu-section {
  margin: 0 16px 24px;
}

.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 18px;
  border: none;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
}

.menu-item-label {
  flex: 1;
  text-align: left;
}

.menu-item-arrow {
  flex-shrink: 0;
  opacity: 0.7;
}

/* 认证模块 */
.cert-section {
  margin: 24px 16px;
}

.cert-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  padding-left: 4px;
}

.cert-refresh-btn {
  flex-shrink: 0;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #7dd3c0;
  background: rgba(62, 224, 184, 0.12);
  border: 1px solid rgba(62, 224, 184, 0.35);
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
}

.cert-refresh-btn:hover:not(:disabled) {
  background: rgba(62, 224, 184, 0.2);
}

.cert-refresh-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cert-card {
  background: rgba(255,255,255,0.08);
  border-radius: 16px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  transition: opacity 0.2s ease;
}
.cert-card.is-loading {
  opacity: 0.85;
}
.cert-loading-placeholder {
  width: 100%;
  height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
  background: rgba(0, 0, 0, 0.18);
}
.cert-loading-spinner {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #a78bfa;
  animation: page-spin 0.75s linear infinite;
}

.cert-image-wrap {
  width: 100%;
  height: 160px;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-in;
  outline: none;
}

.cert-image-wrap:focus-visible {
  box-shadow: inset 0 0 0 2px rgba(167, 139, 250, 0.85);
}

.cert-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  display: block;
  pointer-events: none;
  vertical-align: middle;
}

.cert-placeholder {
  width: 100%;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.08em;
}

.cert-status {
  display: flex;
  align-items: center;
  padding: 16px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #999;
  margin-right: 8px;
}

.status-dot.active {
  background: #52c41a;
  box-shadow: 0 0 8px #52c41a;
}

.status-text {
  font-size: 14px;
  color: rgba(255,255,255,0.8);
}

/* 菜单列表 */
.menu-list {
  margin: 0 16px;
  background: rgba(255,255,255,0.08);
  border-radius: 16px;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

.menu-item:last-child {
  border-bottom: none;
}

.menu-icon {
  margin-right: 12px;
}

.menu-text {
  flex: 1;
  font-size: 15px;
}

/* 退出按钮 */
.logout-btn {
  margin: 32px 16px 0;
  padding: 14px;
  text-align: center;
  background: rgba(255,77,79,0.15);
  color: #ff4d4f;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
}

/* 头像大图弹层 */
.avatar-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.avatar-modal {
  position: relative;
  width: 100%;
  max-width: 340px;
  padding: 24px 20px 20px;
  border-radius: 16px;
  background: linear-gradient(160deg, #2d2654 0%, #1a1530 100%);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
}

.avatar-modal-close {
  position: absolute;
  top: 10px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.avatar-modal-title {
  text-align: center;
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #fff;
}

.avatar-modal-img-wrap {
  width: 200px;
  height: 200px;
  margin: 0 auto 20px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
}

.avatar-modal-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.avatar-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.avatar-modal-btn {
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
}

.avatar-modal-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.avatar-modal-btn.primary {
  background: linear-gradient(90deg, #a78bfa, #7c3aed);
  color: #fff;
}

.avatar-modal-btn.secondary {
  background: rgba(255, 255, 255, 0.12);
  color: #e8e4ff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.avatar-file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

/* 认证图大图预览 */
.cert-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.cert-modal-box {
  position: relative;
  width: 100%;
  max-width: min(100%, 400px);
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: linear-gradient(160deg, #2d2654 0%, #1a1530 100%);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
  overflow: hidden;
}

.cert-modal-close {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 2;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.cert-modal-title {
  flex-shrink: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  padding: 18px 40px 10px;
  color: #fff;
}

.cert-modal-img-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px 20px;
  max-height: min(72vh, 560px);
}

.cert-modal-img {
  max-width: 100%;
  max-height: min(68vh, 520px);
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  border-radius: 8px;
  display: block;
}

.cert-modal-actions {
  display: flex;
  justify-content: center;
  padding: 0 16px 16px;
}

.cert-download-btn {
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(90deg, #7c3aed, #a78bfa);
  cursor: pointer;
}
</style>
